/**
 * Demo Users for Testing CraftyJoy Application
 * 
 * This file contains realistic demo users for each role type to test
 * features, permissions, and role-specific workspaces.
 * 
 * ⚠️ PRODUCTION SAFETY: Demo users are automatically disabled in production
 */

import { v4 as uuidv4 } from 'uuid';
import { User, Child, StudentUser, Parent, Teacher, Creator, Guest, Project, AIModel, Classroom } from '../types/user';
import { isDevelopment } from '../lib/config';

// Production safety check
if (!isDevelopment) {
  console.warn('🚨 Demo users are disabled in production environment');
}

interface DemoUserData {
  users: (User | Guest)[];
  relationships: {
    parentChildMap: Record<string, string[]>;
    teacherStudentMap: Record<string, string[]>;
    classrooms: Classroom[];
  };
  sampleProjects: Project[];
  sampleModels: AIModel[];
}

// Generate unique render keys for React components
// This ensures that React can properly track components during re-renders and animations
export const generateUniqueKey = (prefix: string, id?: string): string => {
  // If an ID is provided, use it for consistency across renders
  // Otherwise generate a new UUID for truly unique keys
  const uniqueId = id || uuidv4();
  return `${prefix}-${uniqueId}`;
};

// Helper function specifically for demo user rendering
export const getDemoUserKey = (userType: string, userId?: string): string => {
  return generateUniqueKey('demo-user', userId || userType);
};

// Demo Child Users (Under 13 with Parental Controls)
const demoChildUsers: Child[] = [
  {
    id: 'child-emma-8',
    name: 'Emma Johnson',
    email: 'emma.child@demo.craftyjoy.com',
    type: 'child',
    age: 8,
    grade: '3rd Grade',
    parentId: 'parent-sarah-johnson',
    avatar: 'https://images.pexels.com/photos/1462636/pexels-photo-1462636.jpeg?auto=compress&cs=tinysrgb&w=150',
    createdAt: new Date('2024-09-15').toISOString(),
    updatedAt: new Date('2024-12-15').toISOString(),
    isVerified: true,
    allowedFeatures: {
      canShare: false, // Requires parent approval
      canCollaborate: true,
      canUseAI: true,
      canAccessGallery: true,
      canJoinClassrooms: true,
      maxProjects: 10
    },
    screenTimeLimit: 120, // 2 hours per day
    achievements: ['First Project', 'Voice Commander', 'AI Explorer'],
    totalProjects: 5,
    favoriteBlocks: ['when_started', 'show_text', 'ai_voice_command'],
    lastLoginAt: new Date('2024-12-15T10:30:00Z'),
    safetyTrainingCompleted: true
  },
  {
    id: 'child-alex-7',
    name: 'Alex Chen',
    email: 'alex.child@demo.craftyjoy.com',
    type: 'child',
    age: 7,
    grade: '2nd Grade',
    parentId: 'parent-mike-chen',
    avatar: 'https://images.pexels.com/photos/1379636/pexels-photo-1379636.jpeg?auto=compress&cs=tinysrgb&w=150',
    createdAt: new Date('2024-10-01').toISOString(),
    updatedAt: new Date('2024-12-14').toISOString(),
    isVerified: true,
    allowedFeatures: {
      canShare: true, // Parent approved
      canCollaborate: false, // Restricted by parent
      canUseAI: true,
      canAccessGallery: false, // Parent restricted
      canJoinClassrooms: true,
      maxProjects: 5
    },
    screenTimeLimit: 90, // 1.5 hours per day
    achievements: ['First Project', 'Pattern Maker'],
    totalProjects: 3,
    favoriteBlocks: ['when_started', 'set_background_color', 'play_sound'],
    lastLoginAt: new Date('2024-12-14T15:45:00Z'),
    safetyTrainingCompleted: true
  },
  {
    id: 'child-zoe-12',
    name: 'Zoe Martinez',
    email: 'zoe.child@demo.craftyjoy.com',
    type: 'child',
    age: 12,
    grade: '6th Grade',
    parentId: 'parent-carlos-martinez',
    avatar: 'https://images.pexels.com/photos/1157557/pexels-photo-1157557.jpeg?auto=compress&cs=tinysrgb&w=150',
    createdAt: new Date('2024-08-20').toISOString(),
    updatedAt: new Date('2024-12-15').toISOString(),
    isVerified: true,
    allowedFeatures: {
      canShare: true,
      canCollaborate: true,
      canUseAI: true,
      canAccessGallery: true,
      canJoinClassrooms: true,
      maxProjects: 15
    },
    screenTimeLimit: 180, // 3 hours per day
    achievements: ['First Project', 'AI Master', 'Creative Coder', 'Voice Commander', 'Collaboration Star'],
    totalProjects: 12,
    favoriteBlocks: ['ai_classify_image', 'ai_train_image_model', 'if_then', 'repeat_times'],
    lastLoginAt: new Date('2024-12-15T16:20:00Z'),
    safetyTrainingCompleted: true
  }
];

// Demo Student Users (13+ with Full Access)
const demoStudentUsers: StudentUser[] = [
  {
    id: 'student-maya-14',
    name: 'Maya Patel',
    email: 'maya.student@demo.craftyjoy.com',
    type: 'student',
    age: 14,
    grade: '8th Grade',
    avatar: 'https://images.pexels.com/photos/1516680/pexels-photo-1516680.jpeg?auto=compress&cs=tinysrgb&w=150',
    createdAt: new Date('2024-09-05').toISOString(),
    updatedAt: new Date('2024-12-15').toISOString(),
    isVerified: true,
    classrooms: ['classroom-mrs-davis-8th', 'classroom-coding-club'],
    achievements: [
      { id: 'ach-1', name: 'First Project', description: 'Completed your first project', icon: 'rocket', dateEarned: new Date().toISOString(), category: 'coding' },
      { id: 'ach-2', name: 'AI Expert', description: 'Mastered advanced AI blocks', icon: 'brain', dateEarned: new Date().toISOString(), category: 'ai' },
    ],
    totalProjects: 25,
    publicProjects: 8,
    templatesCreated: 3,
    collaborations: 12,
    favoriteSubjects: ['Computer Science', 'Art', 'Math'],
    programmingLanguages: ['Blocks', 'JavaScript', 'Python'],
    lastLoginAt: new Date('2024-12-15T14:10:00Z'),
    skillLevel: 'intermediate'
  },
  {
    id: 'student-jordan-16',
    name: 'Jordan Williams',
    email: 'jordan.student@demo.craftyjoy.com',
    type: 'student',
    age: 16,
    grade: '10th Grade',
    avatar: 'https://images.pexels.com/photos/1462637/pexels-photo-1462637.jpeg?auto=compress&cs=tinysrgb&w=150',
    createdAt: new Date('2024-07-15').toISOString(),
    updatedAt: new Date('2024-12-15').toISOString(),
    isVerified: true,
    classrooms: ['classroom-mr-thompson-cs', 'classroom-robotics-team'],
    achievements: [
        { id: 'ach-1', name: 'First Project', description: 'Completed your first project', icon: 'rocket', dateEarned: new Date().toISOString(), category: 'coding' },
    ],
    totalProjects: 45,
    publicProjects: 20,
    templatesCreated: 8,
    collaborations: 30,
    favoriteSubjects: ['Computer Science', 'AI/ML', 'Engineering'],
    programmingLanguages: ['Blocks', 'JavaScript', 'Python', 'Java'],
    lastLoginAt: new Date('2024-12-15T13:55:00Z'),
    skillLevel: 'advanced',
    mentorStatus: 'active',
    researchProjects: ['Voice AI Optimization', 'Computer Vision for Education']
  }
];

// Demo Parent Users
const demoParentUsers: Parent[] = [
  {
    id: 'parent-sarah-johnson',
    name: 'Sarah Johnson',
    email: 'sarah.parent@demo.craftyjoy.com',
    type: 'parent',
    avatar: 'https://images.pexels.com/photos/1181686/pexels-photo-1181686.jpeg?auto=compress&cs=tinysrgb&w=150',
    createdAt: new Date('2024-09-10').toISOString(),
    updatedAt: new Date('2024-12-15').toISOString(),
    isVerified: true,
    children: ['child-emma-8'],
    subscriptionTier: 'family',
    totalChildren: 1,
    familyScreenTime: {
      dailyLimit: 120,
      weeklyLimit: 600,
      currentUsage: 85
    },
    parentalControls: {
      contentFiltering: 'strict',
      allowPublicSharing: false,
      approvalRequired: true,
      activityReports: 'weekly'
    },
    notificationPreferences: {
      childActivity: true,
      weeklyReports: true,
      safetyAlerts: true,
      achievements: true
    },
    lastLoginAt: new Date('2024-12-15T08:30:00Z'),
    occupation: 'Marketing Manager',
    techExperience: 'intermediate'
  },
  {
    id: 'parent-mike-chen',
    name: 'Mike Chen',
    email: 'mike.parent@demo.craftyjoy.com',
    type: 'parent',
    avatar: 'https://images.pexels.com/photos/1043471/pexels-photo-1043471.jpeg?auto=compress&cs=tinysrgb&w=150',
    createdAt: new Date('2024-09-25').toISOString(),
    updatedAt: new Date('2024-12-14').toISOString(),
    isVerified: true,
    children: ['child-alex-7'],
    subscriptionTier: 'premium',
    totalChildren: 1,
    familyScreenTime: {
      dailyLimit: 90,
      weeklyLimit: 450,
      currentUsage: 65
    },
    parentalControls: {
      contentFiltering: 'moderate',
      allowPublicSharing: true,
      approvalRequired: true,
      activityReports: 'daily'
    },
    notificationPreferences: {
      childActivity: true,
      weeklyReports: true,
      safetyAlerts: true,
      achievements: true
    },
    lastLoginAt: new Date('2024-12-14T19:15:00Z'),
    occupation: 'Software Engineer',
    techExperience: 'advanced'
  },
  {
    id: 'parent-carlos-martinez',
    name: 'Carlos Martinez',
    email: 'carlos.parent@demo.craftyjoy.com',
    type: 'parent',
    avatar: 'https://images.pexels.com/photos/1212984/pexels-photo-1212984.jpeg?auto=compress&cs=tinysrgb&w=150',
    createdAt: new Date('2024-08-15').toISOString(),
    updatedAt: new Date('2024-12-15').toISOString(),
    isVerified: true,
    children: ['child-zoe-12'],
    subscriptionTier: 'family',
    totalChildren: 1,
    familyScreenTime: {
      dailyLimit: 180,
      weeklyLimit: 900,
      currentUsage: 140
    },
    parentalControls: {
      contentFiltering: 'minimal',
      allowPublicSharing: true,
      approvalRequired: false, // Trusts 12-year-old
      activityReports: 'weekly'
    },
    notificationPreferences: {
      childActivity: false,
      weeklyReports: true,
      safetyAlerts: true,
      achievements: true
    },
    lastLoginAt: new Date('2024-12-15T07:45:00Z'),
    occupation: 'Teacher',
    techExperience: 'intermediate'
  }
];

// Demo Teacher Users
const demoTeacherUsers: Teacher[] = [
  {
    id: 'teacher-mrs-davis',
    name: 'Jennifer Davis',
    email: 'jennifer.davis@lincolnelementary.edu',
    type: 'teacher',
    avatar: 'https://images.pexels.com/photos/1181424/pexels-photo-1181424.jpeg?auto=compress&cs=tinysrgb&w=150',
    createdAt: new Date('2024-08-01').toISOString(),
    updatedAt: new Date('2024-12-15').toISOString(),
    isVerified: true,
    school: 'Lincoln Elementary School',
    classrooms: ['classroom-mrs-davis-8th', 'classroom-coding-club'],
    verificationStatus: 'verified',
    teacherCode: 'TEACH-JD-2024',
    totalStudents: 28,
    activeAssignments: 5,
    completedAssignments: 15,
    subjects: ['Computer Science', 'Math', 'Technology'],
    gradesTaught: ['6th', '7th', '8th'],
    yearsExperience: 8,
    certifications: ['Computer Science Teaching', 'Google for Education', 'Microsoft Educator'],
    lastLoginAt: new Date('2024-12-15T09:20:00Z'),
    classroomManagement: {
      allowStudentCollaboration: true,
      restrictAIFeatures: false,
      parentNotifications: true,
      gradingEnabled: true
    },
    teachingPhilosophy: 'Hands-on learning through creative technology projects'
  },
  {
    id: 'teacher-mr-thompson',
    name: 'Robert Thompson',
    email: 'robert.thompson@techcityhs.edu',
    type: 'teacher',
    avatar: 'https://images.pexels.com/photos/1438081/pexels-photo-1438081.jpeg?auto=compress&cs=tinysrgb&w=150',
    createdAt: new Date('2024-07-10').toISOString(),
    updatedAt: new Date('2024-12-15').toISOString(),
    isVerified: true,
    school: 'Tech City High School',
    classrooms: ['classroom-mr-thompson-cs', 'classroom-robotics-team'],
    verificationStatus: 'verified',
    teacherCode: 'TEACH-RT-2024',
    totalStudents: 85,
    activeAssignments: 8,
    completedAssignments: 25,
    subjects: ['Advanced Computer Science', 'AI & Machine Learning', 'Robotics'],
    gradesTaught: ['9th', '10th', '11th', '12th'],
    yearsExperience: 12,
    certifications: [
      'Computer Science AP Teaching', 'MIT AI Teaching Certificate', 
      'Google AI for Education', 'AWS Educator'
    ],
    lastLoginAt: new Date('2024-12-15T11:30:00Z'),
    classroomManagement: {
      allowStudentCollaboration: true,
      restrictAIFeatures: false,
      parentNotifications: false, // High school students
      gradingEnabled: true
    },
    teachingPhilosophy: 'Preparing students for the AI-driven future through practical application',
    researchInterests: ['Educational AI', 'Student Engagement', 'Computer Vision']
  }
];

// Demo Creator Users
const demoCreatorUsers: Creator[] = [
  {
    id: 'creator-alex-kim',
    name: 'Alex Kim',
    email: 'alex.creator@demo.craftyjoy.com',
    type: 'creator',
    avatar: 'https://images.pexels.com/photos/1516680/pexels-photo-1516680.jpeg?auto=compress&cs=tinysrgb&w=150',
    createdAt: new Date('2024-06-15').toISOString(),
    updatedAt: new Date('2024-12-15').toISOString(),
    isVerified: true,
    bio: 'AI education specialist creating interactive learning experiences for kids. Former Google AI researcher turned educational content creator.',
    templates: ['template-voice-pet', 'template-ai-art-studio', 'template-smart-home'],
    earnings: 12750.50,
    totalSales: 1428,
    followerCount: 3420,
    stripeAccountId: 'acct_demo_alex_kim',
    verificationStatus: 'verified',
    specialties: ['AI Education', 'Voice Recognition', 'Computer Vision'],
    ratingAverage: 4.8,
    totalReviews: 234,
    templatesPublished: 12,
    templatesInDevelopment: 3,
    lastLoginAt: new Date('2024-12-15T16:45:00Z'),
    socialLinks: {
      twitter: '@alexkim_edu',
      youtube: 'alexkim-ai-education',
      website: 'alexkim-learns.com'
    },
    achievements: [
      'Top Creator 2024', 'Best AI Template', 'Community Favorite',
      '1000+ Sales', 'Educational Excellence'
    ],
    submittedTemplates: []
  },
  {
    id: 'creator-sofia-garcia',
    name: 'Sofia Garcia',
    email: 'sofia.creator@demo.craftyjoy.com',
    type: 'creator',
    avatar: 'https://images.pexels.com/photos/1181686/pexels-photo-1181686.jpeg?auto=compress&cs=tinysrgb&w=150',
    createdAt: new Date('2024-05-20').toISOString(),
    updatedAt: new Date('2024-12-15').toISOString(),
    isVerified: true,
    bio: 'Elementary school teacher and creative coding advocate. Passionate about making AI accessible and fun for young learners.',
    templates: ['template-story-maker', 'template-math-games', 'template-ai-assistant'],
    earnings: 8430.25,
    totalSales: 892,
    followerCount: 2150,
    stripeAccountId: 'acct_demo_sofia_garcia',
    verificationStatus: 'verified',
    specialties: ['Elementary Education', 'Creative Coding', 'Storytelling'],
    ratingAverage: 4.9,
    totalReviews: 145,
    templatesPublished: 8,
    templatesInDevelopment: 2,
    lastLoginAt: new Date('2024-12-15T12:10:00Z'),
    socialLinks: {
      instagram: '@sofia_codes',
      website: 'creative-coding-classroom.com'
    },
    achievements: [
      'Educator of the Year', 'Most Creative Templates', 'Student Favorite',
      'Rising Star', 'Community Champion'
    ],
    submittedTemplates: []
  }
];

// Demo Guest User
export const demoGuestUser: Guest = {
  id: 'guest-demo-session',
  type: 'guest',
  name: 'Guest User',
  sessionId: 'guest-session-' + Date.now(),
  tempProjects: ['guest-project-1', 'guest-project-2'],
  createdAt: new Date().toISOString(),
  allowedFeatures: {
    canCreateProjects: true,
    canUseBasicAI: true,
    canSaveLocally: true,
    maxProjects: 3,
    maxBlocks: 50,
    sessionDuration: 60 // minutes
  },
  lastActivity: new Date(),
  conversionPrompts: {
    showUpgradeAfter: 15, // minutes
    showSavePrompt: true,
    showSharePrompt: false
  }
};

// Sample Projects for Demo Users
const demoProjects: Project[] = [
  // Emma's Projects (Child)
  {
    id: 'project-emma-voice-pet',
    name: 'My Voice Pet',
    description: 'A virtual pet that responds to voice commands!',
    isPublic: false, // Parent approval pending
    tags: ['voice', 'pet', 'ai'],
    createdAt: new Date('2024-12-10'),
    lastModified: new Date('2024-12-10').toISOString(),
    ownerId: 'child-emma-8',
    content: {},
  },
  {
    id: 'project-emma-color-mixer',
    name: 'Magic Color Mixer',
    description: 'Mix colors with your voice and see the magic!',
    isPublic: true, // Parent approved
    tags: ['colors', 'art', 'voice'],
    createdAt: new Date('2024-12-05'),
    lastModified: new Date('2024-12-05').toISOString(),
    ownerId: 'child-emma-8',
    content: {},
  },

  // Maya's Projects (Student)
  {
    id: 'project-maya-ai-tutor',
    name: 'AI Study Buddy',
    description: 'An AI-powered study assistant that helps with homework',
    isPublic: true,
    tags: ['ai', 'education', 'chatbot'],
    createdAt: new Date('2024-12-12'),
    lastModified: new Date('2024-12-12').toISOString(),
    ownerId: 'student-maya-14',
    content: {},
  },
  {
    id: 'project-jordan-ml-game',
    name: 'AI Game Designer',
    description: 'AI that generates game levels based on player behavior',
    isPublic: true,
    tags: ['ai', 'games', 'machine-learning'],
    createdAt: new Date('2024-12-14'),
    lastModified: new Date('2024-12-14').toISOString(),
    ownerId: 'student-jordan-16',
    content: {},
  },
  {
    id: 'project-maya-image-classifier',
    name: 'Recycling Sorter AI',
    description: 'AI that identifies recyclable materials using your camera',
    isPublic: true,
    tags: ['ai', 'environment', 'image-recognition'],
    createdAt: new Date('2024-12-08'),
    lastModified: new Date('2024-12-08').toISOString(),
    ownerId: 'student-maya-14',
    content: {},
  }
];

// Sample AI Models for Demo Users
const demoAIModels: AIModel[] = [
  {
    id: 'model-emma-pet-voices',
    name: 'Pet Voice Commands',
    type: 'voice',
    createdAt: new Date('2024-12-10').toISOString(),
    trainingData: [],
    modelData: {
      commands: ['sit', 'stay', 'play', 'sleep'],
      accuracy: 0.85,
      trainingSamples: 45
    }
  },
  {
    id: 'model-maya-recycling',
    name: 'Recycling Classifier',
    type: 'image',
    createdAt: new Date('2024-12-08').toISOString(),
    trainingData: [],
    modelData: {
      classes: ['plastic', 'paper', 'glass', 'metal', 'organic'],
      accuracy: 0.92,
      trainingSamples: 250
    }
  },
  {
    id: 'model-jordan-sentiment',
    name: 'Game Feedback Analyzer',
    type: 'text',
    createdAt: new Date('2024-12-14').toISOString(),
    trainingData: [],
    modelData: {
      classes: ['positive', 'negative', 'neutral', 'frustrated', 'excited'],
      accuracy: 0.88,
      trainingSamples: 500
    }
  }
];

// Classroom Data
const demoClassrooms: Classroom[] = [
  {
    id: 'classroom-mrs-davis-8th',
    name: 'Mrs. Davis - 8th Grade Computer Science',
    teacherId: 'teacher-mrs-davis',
    classCode: 'DAVIS24',
    students: ['student-maya-14'],
    description: 'An introductory course to Computer Science',
    assignments: [
      {
        id: 'assignment-intro-ai',
        title: 'Introduction to AI',
        description: 'Create your first AI-powered project',
        dueDate: new Date('2024-12-20').toISOString(),
        submissions: [],
        createdAt: new Date().toISOString()
      },
      {
        id: 'assignment-voice-commands',
        title: 'Voice Command App',
        description: 'Build an app controlled by voice commands',
        dueDate: new Date('2024-12-27').toISOString(),
        submissions: [],
        createdAt: new Date().toISOString()
      }
    ],
    createdAt: new Date('2024-08-15').toISOString(),
  },
  {
    id: 'classroom-mr-thompson-cs',
    name: 'Advanced Computer Science',
    teacherId: 'teacher-mr-thompson',
    classCode: 'ADVCS24',
    students: ['student-jordan-16'],
    description: 'An advanced course in Computer Science and Machine Learning',
    assignments: [
      {
        id: 'assignment-ml-project',
        title: 'Machine Learning Capstone',
        description: 'Create an advanced AI project using multiple models',
        dueDate: new Date('2025-01-15').toISOString(),
        submissions: [],
        createdAt: new Date().toISOString()
      }
    ],
    createdAt: new Date('2024-07-20').toISOString(),
  }
];

// Import showcase projects for enhanced demo
import { showcaseProjects, showcaseAIModels } from './demoShowcaseProjects';

// Complete Demo Data Export
export const demoData: DemoUserData = {
  users: [
    ...demoChildUsers,
    ...demoStudentUsers,
    ...demoParentUsers,
    ...demoTeacherUsers,
    ...demoCreatorUsers,
    demoGuestUser
  ],
  relationships: {
    parentChildMap: {
      'parent-sarah-johnson': ['child-emma-8'],
      'parent-mike-chen': ['child-alex-7'],
      'parent-carlos-martinez': ['child-zoe-12']
    },
    teacherStudentMap: {
      'teacher-mrs-davis': ['student-maya-14'],
      'teacher-mr-thompson': ['student-jordan-16']
    },
    classrooms: demoClassrooms
  },
  sampleProjects: [...demoProjects, ...showcaseProjects],
  sampleModels: [...demoAIModels, ...showcaseAIModels]
};

// Login credentials for easy testing
export const demoLoginCredentials = {
  // Children (require parent login first)
  children: [
    { email: 'emma.child@demo.craftyjoy.com', password: 'demo123', role: 'child', age: 8 },
    { email: 'alex.child@demo.craftyjoy.com', password: 'demo123', role: 'child', age: 7 },
    { email: 'zoe.child@demo.craftyjoy.com', password: 'demo123', role: 'child', age: 12 }
  ],
  // Students
  students: [
    { email: 'maya.student@demo.craftyjoy.com', password: 'demo123', role: 'student', age: 14 },
    { email: 'jordan.student@demo.craftyjoy.com', password: 'demo123', role: 'student', age: 16 }
  ],
  // Parents
  parents: [
    { email: 'sarah.parent@demo.craftyjoy.com', password: 'demo123', role: 'parent', children: ['Emma'] },
    { email: 'mike.parent@demo.craftyjoy.com', password: 'demo123', role: 'parent', children: ['Alex'] },
    { email: 'carlos.parent@demo.craftyjoy.com', password: 'demo123', role: 'parent', children: ['Zoe'] }
  ],
  // Teachers
  teachers: [
    { email: 'jennifer.davis@lincolnelementary.edu', password: 'demo123', role: 'teacher', school: 'Lincoln Elementary' },
    { email: 'robert.thompson@techcityhs.edu', password: 'demo123', role: 'teacher', school: 'Tech City High School' }
  ],
  // Creators
  creators: [
    { email: 'alex.creator@demo.craftyjoy.com', password: 'demo123', role: 'creator', specialty: 'AI Education' },
    { email: 'sofia.creator@demo.craftyjoy.com', password: 'demo123', role: 'creator', specialty: 'Elementary Education' }
  ]
};

// Helper functions removed - use DemoContext methods instead

